import { config } from '../config.ts';
import { createStorageBuffer, createUniformBuffer } from '../gpu/buffers.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import type { HeightmapModule } from './heightmap.ts';
import type { BiomeModule } from './biome.ts';
import type { RoadsModule } from './roads.ts';
import hashWgsl from './hash.wgsl?raw';
import treesWgsl from './trees.wgsl?raw';

const wgsl = hashWgsl + '\n' + treesWgsl;

export interface TreesModule {
  /** Per-instance data: vec3 pos, f32 scale. Stride = 16. */
  readonly instanceBuffer: GPUBuffer;
  /** Single-u32 atomic counter; first u32 = number of instances written. */
  readonly countBuffer: GPUBuffer;
  /** Cap. Same as `config.maxTreeInstances`. */
  readonly maxInstances: number;
  destroy(): void;
}

const INSTANCE_STRIDE = 16;
const CELL_STRIDE = 2;

export async function createTrees(
  device: GPUDevice,
  height: HeightmapModule,
  biome: BiomeModule,
  roads: RoadsModule,
  occupancyView: GPUTextureView,
  opts: { seed: number },
): Promise<TreesModule> {
  const max = config.maxTreeInstances;

  const instanceBuffer = createStorageBuffer(device, max * INSTANCE_STRIDE, {
    label: 'trees.instances',
    usageExtra: GPUBufferUsage.VERTEX,
  });
  const countBuffer = createStorageBuffer(device, 16, { label: 'trees.count' });
  device.queue.writeBuffer(countBuffer, 0, new Uint32Array([0, 0, 0, 0]));

  // Params: u32 seed, f32 worldSize, f32 worldHeight, u32 heightmapSize,
  //         u32 maxInstances, u32 cellStride, u32 _pad0, u32 _pad1 → 32 bytes.
  const paramsBuffer = createUniformBuffer(device, 32, 'trees.params');
  const paramsBytes = new ArrayBuffer(32);
  const paramsU32 = new Uint32Array(paramsBytes);
  const paramsF32 = new Float32Array(paramsBytes);
  paramsU32[0] = opts.seed >>> 0;
  paramsF32[1] = config.worldSize;
  paramsF32[2] = config.worldHeight;
  paramsU32[3] = config.heightmapSize >>> 0;
  paramsU32[4] = max >>> 0;
  paramsU32[5] = CELL_STRIDE >>> 0;
  paramsU32[6] = 0;
  paramsU32[7] = 0;
  device.queue.writeBuffer(paramsBuffer, 0, paramsBytes);

  const pipeline = createComputePipeline(device, wgsl, 'main', 'auto', 'trees');
  // Post-pass that clamps the atomic counter back to maxInstances — drawIndirect
  // consumes the counter as instanceCount, and overflowing appends only drop the
  // write, not the increment. Its auto layout reflects just count + params.
  const clampPipeline = createComputePipeline(device, wgsl, 'clamp_count', 'auto', 'trees.clamp');

  const bindGroup = device.createBindGroup({
    label: 'trees.bindGroup',
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: height.view },
      { binding: 1, resource: biome.view },
      { binding: 2, resource: { buffer: countBuffer } },
      { binding: 3, resource: { buffer: instanceBuffer } },
      { binding: 4, resource: { buffer: paramsBuffer } },
      { binding: 5, resource: roads.maskView },
      { binding: 6, resource: occupancyView },
    ],
  });

  const clampBindGroup = device.createBindGroup({
    label: 'trees.clamp.bindGroup',
    layout: clampPipeline.getBindGroupLayout(0),
    entries: [
      { binding: 2, resource: { buffer: countBuffer } },
      { binding: 4, resource: { buffer: paramsBuffer } },
    ],
  });

  const candidates = Math.ceil(config.heightmapSize / CELL_STRIDE);
  const groups = Math.ceil(candidates / 8);

  const encoder = device.createCommandEncoder({ label: 'trees.encoder' });
  const pass = encoder.beginComputePass({ label: 'trees.pass' });
  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  pass.dispatchWorkgroups(groups, groups, 1);
  pass.end();
  const clampPass = encoder.beginComputePass({ label: 'trees.clamp.pass' });
  clampPass.setPipeline(clampPipeline);
  clampPass.setBindGroup(0, clampBindGroup);
  clampPass.dispatchWorkgroups(1, 1, 1);
  clampPass.end();
  device.queue.submit([encoder.finish()]);

  paramsBuffer.destroy();

  return {
    instanceBuffer,
    countBuffer,
    maxInstances: max,
    destroy() {
      instanceBuffer.destroy();
      countBuffer.destroy();
    },
  };
}
